import { throttle, updateHeaderHeights } from '@theme/utilities';

const HEADER_CLASSES = {
  IS_TRANSPARENT: 'is-transparent',
  IS_SCROLLED: 'is-scrolled',
};

const VALUES = {
  SCROLL_THRESHOLD: 10,
  THROTTLE_DELAY: 15,
  MOUSE_ENTER_LEAVE_DELAY: 300,
};

class HeaderMain extends HTMLElement {
  /** Whether the header has transparent mode enabled (set via attribute at init) */
  #supportsTransparent = false;

  /** Current transparent state (changes as user scrolls) */
  #isTransparent = false;

  /** @type {boolean} */
  #isListening = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** Cached pre-header offsetHeight — updated by ResizeObserver */
  #preHeaderHeight = 0;

  /** Tracks whether pre-header was visible on last scroll tick — detects fast-scroll threshold crossing */
  #wasPreHeaderVisible = true;

  connectedCallback() {
    this.#supportsTransparent = this.hasAttribute(HEADER_CLASSES.IS_TRANSPARENT);
    this.#isTransparent = this.#supportsTransparent;

    updateHeaderHeights();
    this.#setupResizeObserver();
    this.#wasPreHeaderVisible = window.scrollY < this.#preHeaderHeight;
    this.#setupScrollListener();
  }

  disconnectedCallback() {
    if (this.#isListening) {
      this.#isListening = false;
      window.removeEventListener('scroll', this.#throttledHandleScroll);
    }
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  #setupResizeObserver() {
    const preHeader = document.querySelector('#pre-header');
    this.#resizeObserver = new ResizeObserver(() => {
      if (preHeader instanceof HTMLElement) {
        this.#preHeaderHeight = preHeader.offsetHeight;
      }
      updateHeaderHeights();
    });
    this.#resizeObserver.observe(this);
    if (preHeader instanceof HTMLElement) {
      this.#preHeaderHeight = preHeader.offsetHeight;
      this.#resizeObserver.observe(preHeader);
    }
  }

  #setupScrollListener() {
    if (this.#isListening) return;
    this.#isListening = true;
    window.addEventListener('scroll', this.#throttledHandleScroll, { passive: true });
  }

  #handleScroll = () => {
    // Write first: apply is-scrolled attribute before reading layout
    const stateChanged = this.#updateHeaderState();

    const isPreHeaderVisible = window.scrollY < this.#preHeaderHeight;
    const crossedThreshold = isPreHeaderVisible !== this.#wasPreHeaderVisible;

    if (isPreHeaderVisible || crossedThreshold || stateChanged) {
      this.#wasPreHeaderVisible = isPreHeaderVisible;
      updateHeaderHeights();
    }
  };

  #throttledHandleScroll = throttle(this.#handleScroll, VALUES.THROTTLE_DELAY);

  #updateHeaderState = () => {
    if (!this.#supportsTransparent) return false;

    const shouldBeTransparent = window.scrollY < VALUES.SCROLL_THRESHOLD;

    if (shouldBeTransparent === this.#isTransparent) return false;

    this.#isTransparent = shouldBeTransparent;

    if (shouldBeTransparent) {
      this.removeAttribute(HEADER_CLASSES.IS_SCROLLED);
    } else {
      this.setAttribute(HEADER_CLASSES.IS_SCROLLED, '');
    }

    return true;
  };
}

if (!customElements.get('header-main')) {
  customElements.define('header-main', HeaderMain);
}
